// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {
	DESKTOP_DOWNLOAD_URL,
	MACOS_INPUT_MONITORING_PERMISSION_NAME,
	MACOS_MICROPHONE_PERMISSION_NAME,
	MACOS_PRIVACY_AND_SECURITY_SETTINGS_NAME,
	MACOS_SYSTEM_SETTINGS_NAME,
	PRODUCT_NAME,
} from '@app/features/app/config/I18nDisplayConstants';
import {KeybindRecorder} from '@app/features/input/components/KeybindRecorder';
import Keybind, {getDefaultKeybind} from '@app/features/input/state/InputKeybind';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {Select, type SelectOption} from '@app/features/ui/components/form/FormSelect';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {Slider} from '@app/features/ui/components/Slider';
import {RadioGroup, type RadioOption} from '@app/features/ui/radio_group/RadioGroup';
import {openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import {formatRoundedPercentage} from '@app/features/ui/utils/PercentageFormatting';
import {WarningAlert} from '@app/features/ui/warning_alert/WarningAlert';
import {EntranceSoundSection} from '@app/features/user/components/modals/tabs/components/EntranceSoundSection';
import {MicTestSection} from '@app/features/user/components/modals/tabs/components/MicTestSection';
import {useMediaPermission} from '@app/features/user/components/modals/tabs/hooks/useMediaPermission';
import styles from '@app/features/user/components/modals/tabs/UserVoiceTab.module.css';
import * as InputMonitoringPermissionCommands from '@app/features/voice/commands/InputMonitoringPermissionCommands';
import * as VoiceSettingsCommands from '@app/features/voice/commands/VoiceSettingsCommands';
import MediaEngine from '@app/features/voice/engine/MediaEngineFacade';
import NativePermission from '@app/features/voice/state/NativePermission';
import type VoiceSettings from '@app/features/voice/state/VoiceSettings';
import {
	type ExternalAudioProcessorMatch,
	findExternalProcessorForDevice,
} from '@app/features/voice/utils/ExternalAudioProcessor';
import {hasDeviceLabels, resolveEffectiveDeviceId} from '@app/features/voice/utils/VoiceDeviceManager';
import {
	VOICE_AUTOMATIC_GAIN_CONTROL_DESCRIPTOR,
	VOICE_DIRECT_INPUT_PROFILE_DESCRIPTOR,
	VOICE_ECHO_CANCELLATION_DESCRIPTOR,
	VOICE_FOCUSED_VOICE_PROFILE_DESCRIPTOR,
	VOICE_INPUT_DEVICE_DESCRIPTOR,
	VOICE_INPUT_VOLUME_DESCRIPTOR,
	VOICE_NOISE_SUPPRESSION_DESCRIPTOR,
	VOICE_OUTPUT_DEVICE_DESCRIPTOR,
	VOICE_OUTPUT_VOLUME_DESCRIPTOR,
} from '@app/features/voice/utils/VoiceMessageDescriptors';
import type {VoiceProcessingMode} from '@app/features/voice/utils/VoiceProcessingProfile';
import {VOICE_VOLUME_MAX_PERCENT} from '@app/features/voice/utils/VoiceVolumeUtils';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useMemo} from 'react';

const MICROPHONE_DESCRIPTOR = msg({
	message: 'Microphone {shortDeviceId}',
	comment: 'Short label in the voice tab. Keep it concise. Preserve {shortDeviceId}; it is inserted by code.',
});
const DEFAULT_DESCRIPTOR = msg({
	message: 'Default',
	comment: 'Short label in the voice tab. Keep it concise.',
});
const SPEAKER_DESCRIPTOR = msg({
	message: 'Speaker {shortDeviceId}',
	comment: 'Short label in the voice tab. Keep it concise. Preserve {shortDeviceId}; it is inserted by code.',
});
const FOCUSED_VOICE_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Recommended. Cleans up your mic for clear speech.',
	comment: 'Description for the focused voice profile option in the voice tab.',
});
const DIRECT_INPUT_DESCRIPTION_DESCRIPTOR = msg({
	message: "Sends your audio untouched. Best if you're using external audio software.",
	comment: 'Description for the direct input profile option in the voice tab.',
});
const CUSTOM_DESCRIPTOR = msg({
	message: 'Custom',
	comment: 'Short label in the voice tab. Keep it concise.',
});
const CUSTOM_PROFILE_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Adjust each setting yourself: noise suppression, echo cancellation, and gain.',
	comment: 'Description for the custom profile option in the voice tab.',
});
const NOISE_SUPPRESSION_ENHANCED_DESCRIPTOR = msg({
	message: 'Enhanced',
	comment: 'Noise suppression option label in the voice tab (neural filter). Keep it concise.',
});
const NOISE_SUPPRESSION_STANDARD_DESCRIPTOR = msg({
	message: 'Standard',
	comment: 'Noise suppression option label in the voice tab (browser default). Keep it concise.',
});
const NONE_DESCRIPTOR = msg({
	message: 'None',
	comment: 'Short label in the voice tab. Keep it concise.',
});
const PUSH_TO_TALK_LIMITED_DESCRIPTOR = msg({
	message: 'Push-to-talk (limited)',
	comment: 'Short label in the voice tab. Keep it concise.',
});
const DOWNLOAD_DESKTOP_APP_DESCRIPTOR = msg({
	message: 'Download desktop app',
	comment: 'Button or menu action label in the voice tab. Keep it concise.',
});
const I_UNDERSTAND_DESCRIPTOR = msg({
	message: 'I understand',
	comment: 'Short label in the voice tab. Keep it concise.',
});
const PUSH_TO_TALK_DESCRIPTOR = msg({
	message: 'Push-to-talk',
	comment: 'Switch label in the voice tab toggling push-to-talk mode. Keep it concise.',
});
const AUTO_SET_ACTIVITY_THRESHOLD_DESCRIPTOR = msg({
	message: 'Auto-set activity threshold',
	comment: 'Short label in the voice tab. Keep it concise.',
});
const SELECT_VOICE_PROCESSING_DESCRIPTOR = msg({
	message: 'Select voice processing mode',
	comment: 'Aria label for the voice processing radio group in the voice settings tab. Keep it concise.',
});
const ENABLE_PLATFORM_PERMISSION_DESCRIPTOR = msg({
	message: 'Enable {permissionName} permission',
	comment: 'Voice settings CTA label for opening a platform permission prompt.',
});
const PUSH_TO_TALK_BROWSER_LIMITED_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Push-to-talk only works while the browser tab is focused.',
	comment: 'Voice settings warning shown when push-to-talk is enabled in a browser.',
});
const PUSH_TO_TALK_PERMISSION_LIMITED_DESCRIPTION_DESCRIPTOR = msg({
	message:
		'Push-to-talk only works while {productName} is focused until {permissionName} is enabled. After changing the permission, fully quit and restart {productName} so system-wide hotkeys can use it.',
	comment:
		'Voice settings warning shown when macOS Input Monitoring is required for system-wide push-to-talk. {productName} is the app name and {permissionName} is the macOS permission name.',
});
const PRODUCT_NEEDS_MICROPHONE_ACCESS_DESCRIPTOR = msg({
	message: '{productName} needs microphone access to list your devices.',
	comment: 'Voice settings notice shown when microphone permission has not been granted.',
});
const AUTO_GAIN_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Evens out your mic volume. Off when enhanced suppression is on.',
	comment: 'Description for the automatic gain control toggle in the custom voice processing settings.',
});

type NoiseSuppressionMethod = 'enhanced' | 'standard' | 'none';

interface VoiceTabProps {
	voiceSettings: typeof VoiceSettings;
	hasPremium: boolean;
	autoRequestPermission?: boolean;
}

function resolveNoiseSuppressionMethod(deepFilterEnabled: boolean, browserNsEnabled: boolean): NoiseSuppressionMethod {
	if (deepFilterEnabled) return 'enhanced';
	if (browserNsEnabled) return 'standard';
	return 'none';
}

export const VoiceTab: React.FC<VoiceTabProps> = observer(({voiceSettings, autoRequestPermission = true}) => {
	const {i18n} = useLingui();
	const {
		inputDeviceId,
		outputDeviceId,
		inputVolume,
		outputVolume,
		echoCancellation,
		noiseSuppression,
		autoGainControl,
		deepFilterNoiseSuppression,
		vadThreshold,
		vadAutoSensitivity,
	} = voiceSettings;
	const {
		devices,
		status: permissionStatus,
		requestPermission,
	} = useMediaPermission('audio', {
		autoRequest: autoRequestPermission,
	});
	const isNativeDesktop = NativePermission.isDesktop;
	const isNativeMac = NativePermission.isMacOS;
	const inputMonitoringGranted = NativePermission.isInputMonitoringGranted;
	const inputDevices = useMemo(() => devices.filter((d) => d.kind === 'audioinput'), [devices]);
	const outputDevices = useMemo(() => devices.filter((d) => d.kind === 'audiooutput'), [devices]);
	const transmitMode = Keybind.transmitMode;
	const isPushToTalk = transmitMode === 'voice_push_to_talk';
	const pttPrimary = Keybind.getPrimaryCustomKeybind('voice_push_to_talk');
	const pttCombo = pttPrimary?.combo ?? {key: '', enabled: true, global: true};
	const pttReleaseDelay = Keybind.pushToTalkReleaseDelay;
	const isPttLimited = !isNativeDesktop || (isNativeMac && !inputMonitoringGranted);
	const defaultPttCombo = getDefaultKeybind('voice_push_to_talk', i18n);
	const inputHasLabels = hasDeviceLabels(inputDevices);
	const outputHasLabels = hasDeviceLabels(outputDevices);
	const effectiveInputDeviceId = resolveEffectiveDeviceId(inputDeviceId, inputDevices) ?? 'default';
	const effectiveOutputDeviceId = resolveEffectiveDeviceId(outputDeviceId, outputDevices) ?? 'default';
	const activeInputDevice = inputDevices.find((d) => d.deviceId === effectiveInputDeviceId) ?? null;
	const activeInputLabel = activeInputDevice?.label || null;
	const hasDeviceOverride = voiceSettings.hasVoiceProcessingModeOverrideForDeviceLabel(activeInputLabel);
	const voiceProcessingMode = voiceSettings.getVoiceProcessingModeForDeviceLabel(activeInputLabel);
	const externalProcessor: ExternalAudioProcessorMatch | null = inputHasLabels
		? findExternalProcessorForDevice(effectiveInputDeviceId, inputDevices)
		: null;
	const showExternalProcessorHint = externalProcessor !== null && voiceProcessingMode !== 'studio';
	const handleInputDeviceChange = (value: string) => {
		VoiceSettingsCommands.update({inputDeviceId: value});
	};
	const handleVoiceProcessingModeChange = (mode: VoiceProcessingMode) => {
		VoiceSettingsCommands.setActiveInputVoiceProcessingMode(mode);
	};
	const handleClearDeviceOverride = () => {
		if (activeInputLabel) {
			VoiceSettingsCommands.clearVoiceProcessingModeForDeviceLabel(activeInputLabel);
		}
	};
	const inputDeviceOptions =
		inputDevices.length > 0 && inputHasLabels
			? inputDevices.map((device) => {
					const shortDeviceId = device.deviceId.slice(0, 8);
					return {
						value: device.deviceId,
						label: device.label || i18n._(MICROPHONE_DESCRIPTOR, {shortDeviceId}),
					};
				})
			: [{value: 'default', label: i18n._(DEFAULT_DESCRIPTOR)}];
	const outputDeviceOptions =
		outputDevices.length > 0 && outputHasLabels
			? outputDevices.map((device) => {
					const shortDeviceId = device.deviceId.slice(0, 8);
					return {
						value: device.deviceId,
						label: device.label || i18n._(SPEAKER_DESCRIPTOR, {shortDeviceId}),
					};
				})
			: [{value: 'default', label: i18n._(DEFAULT_DESCRIPTOR)}];
	const profileOptions: Array<RadioOption<VoiceProcessingMode>> = [
		{
			value: 'voice',
			name: i18n._(VOICE_FOCUSED_VOICE_PROFILE_DESCRIPTOR),
			desc: i18n._(FOCUSED_VOICE_DESCRIPTION_DESCRIPTOR),
		},
		{
			value: 'studio',
			name: i18n._(VOICE_DIRECT_INPUT_PROFILE_DESCRIPTOR),
			desc: i18n._(DIRECT_INPUT_DESCRIPTION_DESCRIPTOR),
		},
		{
			value: 'custom',
			name: i18n._(CUSTOM_DESCRIPTOR),
			desc: i18n._(CUSTOM_PROFILE_DESCRIPTION_DESCRIPTOR),
		},
	];
	const noiseSuppressionMethod = resolveNoiseSuppressionMethod(deepFilterNoiseSuppression, noiseSuppression);
	const noiseSuppressionOptions: Array<SelectOption<NoiseSuppressionMethod>> = [
		{value: 'enhanced', label: i18n._(NOISE_SUPPRESSION_ENHANCED_DESCRIPTOR)},
		{value: 'standard', label: i18n._(NOISE_SUPPRESSION_STANDARD_DESCRIPTOR)},
		{value: 'none', label: i18n._(NONE_DESCRIPTOR)},
	];
	const setNoiseSuppressionMethod = (method: NoiseSuppressionMethod) => {
		switch (method) {
			case 'enhanced':
				VoiceSettingsCommands.update({deepFilterNoiseSuppression: true, noiseSuppression: false});
				return;
			case 'standard':
				VoiceSettingsCommands.update({deepFilterNoiseSuppression: false, noiseSuppression: true});
				return;
			case 'none':
				VoiceSettingsCommands.update({deepFilterNoiseSuppression: false, noiseSuppression: false});
				return;
		}
	};
	const setPushToTalkEnabled = (enabled: boolean) => {
		const mode = enabled ? 'voice_push_to_talk' : 'voice_activity';
		if (enabled && !isNativeDesktop) {
			ModalCommands.push(
				modal(() => (
					<ConfirmModal
						title={i18n._(PUSH_TO_TALK_LIMITED_DESCRIPTOR)}
						description={
							<p data-flx="user.voice-tab.set-push-to-talk-enabled.p">
								<Trans>
									In a browser, push-to-talk only works while the {PRODUCT_NAME} tab is focused. Install the desktop app
									for system-wide push-to-talk.
								</Trans>
							</p>
						}
						primaryText={i18n._(DOWNLOAD_DESKTOP_APP_DESCRIPTOR)}
						primaryVariant="primary"
						secondaryText={i18n._(I_UNDERSTAND_DESCRIPTOR)}
						onPrimary={() => {
							void openExternalUrl(DESKTOP_DOWNLOAD_URL);
						}}
						onSecondary={() => {
							Keybind.setTransmitMode(mode);
							MediaEngine.handlePushToTalkModeChange();
						}}
						data-flx="user.voice-tab.set-push-to-talk-enabled.confirm-modal"
					/>
				)),
			);
			return;
		}
		if (enabled && isNativeMac && !inputMonitoringGranted) {
			InputMonitoringPermissionCommands.openInputMonitoringPermissionModal({
				onComplete: () => {
					void NativePermission.recheckInputMonitoring();
					Keybind.setTransmitMode(mode);
					MediaEngine.handlePushToTalkModeChange();
				},
			});
			return;
		}
		if (enabled && NativePermission.isLinuxWaylandDesktop && NativePermission.linuxInputAccessStatus !== 'granted') {
			NativePermission.requestLinuxInputAccessNagbar('push-to-talk');
		}
		Keybind.setTransmitMode(mode);
		MediaEngine.handlePushToTalkModeChange();
	};
	const handleOpenInputMonitoringModal = () => {
		InputMonitoringPermissionCommands.openInputMonitoringPermissionModal({
			onComplete: () => {
				void NativePermission.recheckInputMonitoring();
			},
		});
	};
	const pttAvailable = voiceProcessingMode !== 'studio';
	const showPttDetails = pttAvailable && isPushToTalk;
	const renderPttControls = () => (
		<>
			<Switch
				label={<Trans>Push-to-talk</Trans>}
				description={<Trans>Transmit only while you hold a key or button. Off keeps your mic live.</Trans>}
				value={isPushToTalk}
				onChange={setPushToTalkEnabled}
				ariaLabel={i18n._(PUSH_TO_TALK_DESCRIPTOR)}
				data-flx="user.voice-tab.render-ptt-controls.switch.set-push-to-talk-enabled"
			/>
			{showPttDetails && (
				<div className={styles.pttSettings} data-flx="user.voice-tab.render-ptt-controls.ptt-settings">
					<p className={styles.pttSettingDescription} data-flx="user.voice-tab.render-ptt-controls.ptt-mute-note">
						<Trans>
							While push-to-talk is on, your shortcut controls your microphone, so the mute button is disabled.
						</Trans>
					</p>
					<div className={styles.pttSettingRow} data-flx="user.voice-tab.render-ptt-controls.ptt-setting-row">
						<div className={styles.pttSettingLabel} data-flx="user.voice-tab.render-ptt-controls.ptt-setting-label">
							<Trans>Shortcut</Trans>
						</div>
						<div className={styles.pttKeybindRow} data-flx="user.voice-tab.render-ptt-controls.ptt-keybind-row">
							<KeybindRecorder
								action="voice_push_to_talk"
								value={pttCombo}
								defaultValue={defaultPttCombo}
								onChange={(combo) => {
									Keybind.setPrimaryCustomKeybindCombo('voice_push_to_talk', {
										...combo,
										global: pttCombo.global ?? true,
									});
								}}
								onReset={() => {
									if (defaultPttCombo) {
										Keybind.setPrimaryCustomKeybindCombo('voice_push_to_talk', {
											...defaultPttCombo,
											global: pttCombo.global ?? true,
										});
									}
								}}
								data-flx="user.voice-tab.render-ptt-controls.keybind-recorder.set-primary-custom-keybind-combo"
							/>
						</div>
					</div>
					<div className={styles.pttSettingRow} data-flx="user.voice-tab.render-ptt-controls.ptt-setting-row--2">
						<div className={styles.pttSettingLabel} data-flx="user.voice-tab.render-ptt-controls.ptt-setting-label--2">
							<Trans>Release delay</Trans>
						</div>
						<p
							className={styles.pttSettingDescription}
							data-flx="user.voice-tab.render-ptt-controls.ptt-setting-description"
						>
							<Trans>How long to keep transmitting after you release. Avoids cutting off trailing words.</Trans>
						</p>
						<Slider
							defaultValue={pttReleaseDelay}
							factoryDefaultValue={20}
							minValue={20}
							maxValue={2000}
							step={10}
							markers={[20, 500, 1000, 1500, 2000]}
							stickToMarkers={false}
							onMarkerRender={(value) => `${value}ms`}
							onValueRender={(value) => `${value}ms`}
							onValueChange={(value) => Keybind.setPushToTalkReleaseDelay(value)}
							showResetButton={true}
							data-flx="user.voice-tab.render-ptt-controls.slider"
						/>
					</div>
				</div>
			)}
			{isPushToTalk && isPttLimited && (
				<WarningAlert
					actions={
						!isNativeDesktop ? (
							<Button
								variant="primary"
								small={true}
								onClick={() => void openExternalUrl(DESKTOP_DOWNLOAD_URL)}
								data-flx="user.voice-tab.render-ptt-controls.button.download-desktop-app"
							>
								{i18n._(DOWNLOAD_DESKTOP_APP_DESCRIPTOR)}
							</Button>
						) : (
							<Button
								variant="primary"
								small={true}
								onClick={handleOpenInputMonitoringModal}
								data-flx="user.voice-tab.render-ptt-controls.button.open-input-monitoring-modal"
							>
								{i18n._(ENABLE_PLATFORM_PERMISSION_DESCRIPTOR, {
									permissionName: MACOS_INPUT_MONITORING_PERMISSION_NAME,
								})}
							</Button>
						)
					}
					data-flx="user.voice-tab.render-ptt-controls.warning-alert"
				>
					{!isNativeDesktop
						? i18n._(PUSH_TO_TALK_BROWSER_LIMITED_DESCRIPTION_DESCRIPTOR)
						: i18n._(PUSH_TO_TALK_PERMISSION_LIMITED_DESCRIPTION_DESCRIPTOR, {
								productName: PRODUCT_NAME,
								permissionName: MACOS_INPUT_MONITORING_PERMISSION_NAME,
							})}
				</WarningAlert>
			)}
		</>
	);
	const renderCustomProfile = () => (
		<div className={styles.profileSubSection} data-flx="user.voice-tab.render-custom-profile.profile-sub-section">
			{renderPttControls()}
			<Switch
				label={<Trans>Auto-set activity threshold</Trans>}
				description={<Trans>Sets the threshold for you. Turn off to place it manually.</Trans>}
				value={vadAutoSensitivity}
				onChange={(value) => VoiceSettingsCommands.update({vadAutoSensitivity: value})}
				ariaLabel={i18n._(AUTO_SET_ACTIVITY_THRESHOLD_DESCRIPTOR)}
				data-flx="user.voice-tab.render-custom-profile.switch.update"
			/>
			{!vadAutoSensitivity && (
				<div
					className={styles.sensitivitySliderWrapper}
					data-flx="user.voice-tab.render-custom-profile.sensitivity-slider-wrapper"
				>
					<div className={styles.sliderLabel} data-flx="user.voice-tab.render-custom-profile.slider-label">
						<Trans>Activity threshold</Trans>
					</div>
					<Slider
						value={vadThreshold}
						defaultValue={vadThreshold}
						factoryDefaultValue={50}
						minValue={0}
						maxValue={100}
						step={1}
						onValueRender={formatRoundedPercentage}
						asValueChanges={(value) => VoiceSettingsCommands.update({vadThreshold: value})}
						onValueChange={(value) => VoiceSettingsCommands.update({vadThreshold: value})}
						showResetButton={true}
						data-flx="user.voice-tab.render-custom-profile.slider"
					/>
				</div>
			)}
			<div data-flx="user.voice-tab.render-custom-profile.div">
				<div className={styles.sliderLabel} data-flx="user.voice-tab.render-custom-profile.slider-label--2">
					{i18n._(VOICE_NOISE_SUPPRESSION_DESCRIPTOR)}
				</div>
				<p
					className={styles.audioProcessingDescription}
					data-flx="user.voice-tab.render-custom-profile.audio-processing-description"
				>
					<Trans>Enhanced gives the cleanest audio. Standard is lighter on your CPU. None turns it off.</Trans>
				</p>
				<Select
					label={i18n._(VOICE_NOISE_SUPPRESSION_DESCRIPTOR)}
					value={noiseSuppressionMethod}
					options={noiseSuppressionOptions}
					onChange={setNoiseSuppressionMethod}
					data-flx="user.voice-tab.render-custom-profile.select.set-noise-suppression-method"
				/>
			</div>
			<Switch
				label={i18n._(VOICE_ECHO_CANCELLATION_DESCRIPTOR)}
				description={<Trans>Stops your speakers from looping back into your mic.</Trans>}
				value={echoCancellation}
				onChange={(value) => VoiceSettingsCommands.update({echoCancellation: value})}
				ariaLabel={i18n._(VOICE_ECHO_CANCELLATION_DESCRIPTOR)}
				data-flx="user.voice-tab.render-custom-profile.switch.update--2"
			/>
			<Switch
				label={i18n._(VOICE_AUTOMATIC_GAIN_CONTROL_DESCRIPTOR)}
				description={i18n._(AUTO_GAIN_DESCRIPTION_DESCRIPTOR)}
				value={autoGainControl}
				disabled={deepFilterNoiseSuppression}
				onChange={(value) => VoiceSettingsCommands.update({autoGainControl: value})}
				ariaLabel={i18n._(VOICE_AUTOMATIC_GAIN_CONTROL_DESCRIPTOR)}
				data-flx="user.voice-tab.render-custom-profile.switch.update-auto-gain-control"
			/>
		</div>
	);
	return (
		<>
			{devices.length === 0 && permissionStatus !== 'loading' && permissionStatus !== 'granted' ? (
				<div className={styles.deviceNotice} data-flx="user.voice-tab.device-notice">
					<div className={styles.deviceNoticeText} data-flx="user.voice-tab.device-notice-text">
						<div className={styles.deviceNoticeTitle} data-flx="user.voice-tab.device-notice-title">
							<Trans>No microphone found</Trans>
						</div>
						<p className={styles.deviceNoticeDescription} data-flx="user.voice-tab.device-notice-description">
							{permissionStatus === 'denied' ? (
								isNativeDesktop ? (
									<Trans>
										Allow {PRODUCT_NAME} to access your microphone in {MACOS_SYSTEM_SETTINGS_NAME} →{' '}
										{MACOS_PRIVACY_AND_SECURITY_SETTINGS_NAME} → {MACOS_MICROPHONE_PERMISSION_NAME}.
									</Trans>
								) : (
									<Trans>
										Allow {PRODUCT_NAME} to access your microphone. Check your browser's address bar or settings.
									</Trans>
								)
							) : (
								i18n._(PRODUCT_NEEDS_MICROPHONE_ACCESS_DESCRIPTOR, {productName: PRODUCT_NAME})
							)}
						</p>
					</div>
					<Button
						variant="secondary"
						small={true}
						onClick={() => {
							void requestPermission();
						}}
						data-flx="user.voice-tab.button"
					>
						<Trans>Allow microphone</Trans>
					</Button>
				</div>
			) : null}
			<div data-flx="user.voice-tab.div">
				<Select
					label={i18n._(VOICE_INPUT_DEVICE_DESCRIPTOR)}
					value={effectiveInputDeviceId}
					options={inputDeviceOptions}
					onChange={handleInputDeviceChange}
					data-flx="user.voice-tab.select.input-device-change"
				/>
			</div>
			<div data-flx="user.voice-tab.div--2">
				<Select
					label={i18n._(VOICE_OUTPUT_DEVICE_DESCRIPTOR)}
					value={effectiveOutputDeviceId}
					options={outputDeviceOptions}
					onChange={(value) => VoiceSettingsCommands.update({outputDeviceId: value})}
					data-flx="user.voice-tab.select.update"
				/>
			</div>
			<div data-flx="user.voice-tab.div--3">
				<div className={styles.sliderLabel} data-flx="user.voice-tab.slider-label">
					{i18n._(VOICE_INPUT_VOLUME_DESCRIPTOR)}
				</div>
				<Slider
					value={inputVolume}
					defaultValue={inputVolume}
					factoryDefaultValue={100}
					minValue={0}
					maxValue={VOICE_VOLUME_MAX_PERCENT}
					step={1}
					markers={[0, 50, 100, 150, 200]}
					stickToMarkers={false}
					onMarkerRender={formatRoundedPercentage}
					onValueRender={formatRoundedPercentage}
					onValueChange={(value) => VoiceSettingsCommands.update({inputVolume: value})}
					showResetButton={true}
					data-flx="user.voice-tab.slider"
				/>
			</div>
			<div data-flx="user.voice-tab.div--4">
				<div className={styles.sliderLabel} data-flx="user.voice-tab.slider-label--2">
					{i18n._(VOICE_OUTPUT_VOLUME_DESCRIPTOR)}
				</div>
				<Slider
					value={outputVolume}
					defaultValue={outputVolume}
					factoryDefaultValue={100}
					minValue={0}
					maxValue={VOICE_VOLUME_MAX_PERCENT}
					step={1}
					markers={[0, 50, 100, 150, 200]}
					stickToMarkers={false}
					onMarkerRender={formatRoundedPercentage}
					onValueRender={formatRoundedPercentage}
					onValueChange={(value) => VoiceSettingsCommands.update({outputVolume: value})}
					showResetButton={true}
					data-flx="user.voice-tab.slider--2"
				/>
			</div>
			<div className={styles.audioProcessing} data-flx="user.voice-tab.audio-processing">
				<div className={styles.audioProcessingLabel} data-flx="user.voice-tab.audio-processing-label">
					<Trans>Voice processing</Trans>
				</div>
				{showExternalProcessorHint && externalProcessor != null && (
					<WarningAlert
						link={{
							label: <Trans>Switch to direct input</Trans>,
							onClick: () => handleVoiceProcessingModeChange('studio'),
						}}
						data-flx="user.voice-tab.warning-alert"
					>
						<Trans>
							Looks like you're using {externalProcessor.name}. Stacking {PRODUCT_NAME}'s processing on top can cause
							pumping or distortion. Direct input passes your audio through untouched.
						</Trans>
					</WarningAlert>
				)}
				<div className={styles.audioProcessingCard} data-flx="user.voice-tab.audio-processing-card">
					<RadioGroup
						options={profileOptions}
						value={voiceProcessingMode}
						onChange={handleVoiceProcessingModeChange}
						aria-label={i18n._(SELECT_VOICE_PROCESSING_DESCRIPTOR)}
						data-flx="user.voice-tab.radio-group.voice-processing-mode-change"
					/>
					{activeInputLabel != null && (
						<p className={styles.profileHelperText} data-flx="user.voice-tab.profile-helper-text">
							{hasDeviceOverride ? (
								<>
									<Trans>
										Saved for <strong data-flx="user.voice-tab.strong">{activeInputLabel}</strong>. Reapplied whenever
										you switch back.
									</Trans>{' '}
									<Button
										variant="secondary"
										small={true}
										onClick={handleClearDeviceOverride}
										data-flx="user.voice-tab.button.clear-device-override"
									>
										<Trans>Use the default profile</Trans>
									</Button>
								</>
							) : (
								<Trans>
									Your choice saves for <strong data-flx="user.voice-tab.strong--2">{activeInputLabel}</strong> and
									reapplies next time you use this input.
								</Trans>
							)}
						</p>
					)}
					{voiceProcessingMode === 'voice' && (
						<div className={styles.profileSubSection} data-flx="user.voice-tab.profile-sub-section">
							{renderPttControls()}
						</div>
					)}
					{voiceProcessingMode === 'studio' && (
						<>
							<p className={styles.profileHelperText} data-flx="user.voice-tab.profile-helper-text--2">
								<Trans>
									Direct input stays live and ignores push-to-talk. Echo cancellation, noise suppression, and gain
									control are off.
								</Trans>
							</p>
							{pttCombo?.key && isPushToTalk && (
								<WarningAlert data-flx="user.voice-tab.warning-alert--2">
									<Trans>Push-to-talk is ignored in direct input. Switch to focused voice or custom to use it.</Trans>
								</WarningAlert>
							)}
						</>
					)}
					{voiceProcessingMode === 'custom' && renderCustomProfile()}
				</div>
			</div>
			<MicTestSection
				settings={{
					inputDeviceId,
					outputDeviceId,
					inputVolume,
					outputVolume,
					echoCancellation,
					noiseSuppression,
					autoGainControl,
					deepFilterNoiseSuppression,
					voiceProcessingMode,
				}}
				data-flx="user.voice-tab.mic-test-section"
			/>
			<EntranceSoundSection data-flx="user.voice-tab.entrance-sound-section" />
		</>
	);
});
